import { type FC, type RefObject, Suspense, useCallback, useRef, useState } from 'react';
import { type RelayProp, graphql, createFragmentContainer } from 'react-relay';
import classnames from 'classnames';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient.ts';
import { useIntl } from 'dibs-react-intl';

import { messages } from '../messages/pdpDealerDetailsMessages.jsx';
import { trackSellerDetailsView } from '../tracking/pdpTracking.js';
import {
    MAKE_OFFER,
    CONTACT_SELLER,
} from '../PdpSharedContactTabsVariant/PdpSharedSellerTabsContants.jsx';

// components
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { PdpSharedDealerDistinction } from '../PdpSharedDealerDistinctions/PdpSharedDealerDistinction.tsx';
import { PdpSharedCallDealerCta } from '../PdpSharedCallDealerCta/PdpSharedCallDealerCta.tsx';
import { ContactDealerInlineFormLoader } from 'dibs-buyer-layout/exports/ContactDealerInlineFormLoader.jsx';
import { PdpSharedSellerTabsVariantLazy } from '../PdpSharedContactTabsVariant/PdpSharedSellerTabsVariantLazy.tsx';
import { PdpSharedDealerDetailsBio } from '../PdpSharedDealerDetailsBio/PdpSharedDealerDetailsBio.tsx';
import { PdpSharedDealerDetailsHeader } from '../PdpSharedDealerDetailsHeader/PdpSharedDealerDetailsHeader.tsx';
import { PdpSharedDealerInfo } from '../PdpSharedDealerInfo/PdpSharedDealerInfo.tsx';
import { SellerAwayBanner } from 'dibs-presence/exports/SellerAwayBanner.ts';
import { useIsSellerBrandingV2Variant } from '../helpers/sellerBrandingV2TestHelpers.ts';
import { PdpSharedBuyerIncentivesCompact } from '../shared/PdpSharedBuyerIncentivesCompact/PdpSharedBuyerIncentivesCompact.tsx';
import { PdpSharedSellerBrandingInfo } from '../shared/PdpSharedSellerBranding/PdpSharedSellerBrandingInfo.tsx';
import { triggerConstants } from 'dibs-tracking';
// types
import { type PdpRespDealerDetails_viewer$data } from './__generated__/PdpRespDealerDetails_viewer.graphql.ts';
import { type PdpRespDealerDetails_item$data } from './__generated__/PdpRespDealerDetails_item.graphql.ts';
import { type PdpRespDealerDetails_user$data } from './__generated__/PdpRespDealerDetails_user.graphql.ts';
import { type PdpRespDealerDetails_sku$data } from './__generated__/PdpRespDealerDetails_sku.graphql.ts';

// styles
import dibsCss from 'dibs-css';
import styles from './PdpRespDealerDetails.scss';

const PdpSharedSellerDetailsReviewContainer = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpSharedSellerDetailsReviewContainer" */ '../shared/PdpSharedSellerDetails/PdpSharedSellerDetailsReviewContainer.tsx'
        )
);

type Props = {
    viewer: PdpRespDealerDetails_viewer$data;
    item: PdpRespDealerDetails_item$data;
    user: PdpRespDealerDetails_user$data | null | undefined;
    sku: PdpRespDealerDetails_sku$data | null | undefined;
    relay: RelayProp;
    afterDisplay: boolean;
    currency: string;
    dataTn?: string;
    loadForms?: boolean;
    hideBorder?: boolean;
    panelRef?: RefObject<HTMLDivElement | null>;
};

const PdpRespDealerDetailsComponent: FC<Props> = ({
    item,
    sku,
    user,
    viewer,
    currency,
    afterDisplay,
    dataTn,
    loadForms: loadFormsRef,
    hideBorder,
    panelRef,
}) => {
    const intl = useIntl();
    const sellerDetailsRef = useRef<HTMLDivElement | null>(null);
    const [loadForms, setLoadForms] = useState(loadFormsRef || false);

    const setWrapperRef = useCallback(
        (node: HTMLDivElement | null) => {
            sellerDetailsRef.current = node;
            if (panelRef) {
                panelRef.current = node;
            }
        },
        [panelRef]
    );

    const { seller, dealerDetailsDisplayInfo } = item;
    const { isRewardsTier2, liveStorefrontProfile } = seller || {};
    const aboutUs = liveStorefrontProfile?.aboutUsUnbranded;
    const isNegotiable = dealerDetailsDisplayInfo?.ctas?.showMakeOffer || false;
    const shouldLoadEmbeddedMakeOffer = afterDisplay && isNegotiable;
    const shouldLoadContactForm = afterDisplay && !isNegotiable;

    const isSellerBrandingV2Variant = useIsSellerBrandingV2Variant();
    const showSellerBranding = isSellerBrandingV2Variant;
    const sellerBrandingImageClassName = classnames(dibsCss.w100px, dibsCss.h100px);
    const showSellerAwayBanner = Boolean(seller?.activeTimeAway?.endDate);
    const isPreTabsPlaceholder = shouldLoadEmbeddedMakeOffer && !loadForms;
    const showBuyerIncentivesBanner =
        isSellerBrandingV2Variant && !showSellerAwayBanner && !isPreTabsPlaceholder;
    const showFallbackSellerAwayBanner = showSellerAwayBanner && !isPreTabsPlaceholder;
    const showHeaderBanner = showBuyerIncentivesBanner || showFallbackSellerAwayBanner;

    if (!seller?.isAccessible) {
        return null;
    }

    const header =
        isRewardsTier2 && aboutUs ? (
            <>
                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.grow,
                        dibsCss.wFull,
                        dibsCss.borderB,
                        dibsCss.borderBSolid,
                        dibsCss.borderBDolphin,
                        dibsCss.pt0,
                        dibsCss.px0,
                        dibsCss.pbLarge,
                        dibsCss.mt0,
                        dibsCss.mx0,
                        dibsCss.mbMedium
                    )}
                    data-tn="pdp-dealer-details-header-bio"
                >
                    <div className={styles.leftSection}>
                        <PdpSharedSellerBrandingInfo
                            seller={seller}
                            showSellerBranding={showSellerBranding}
                            stackedLayout={showSellerBranding}
                            titleClassName={
                                showSellerBranding ? dibsCss.sassyFontHeader : undefined
                            }
                            imageClassName={sellerBrandingImageClassName}
                            trackingTrigger={
                                showSellerBranding
                                    ? triggerConstants.TRIGGER_SELLER_INFORMATION
                                    : undefined
                            }
                        >
                            <PdpSharedDealerDetailsHeader
                                viewer={viewer}
                                seller={seller}
                                user={user}
                                afterDisplay={afterDisplay}
                                showSellerBranding={showSellerBranding}
                            />
                        </PdpSharedSellerBrandingInfo>
                    </div>
                    <div
                        className={classnames(
                            styles.detailsBio,
                            dibsCss.plLarger,
                            styles.rightSection
                        )}
                    >
                        <PdpSharedDealerDetailsBio seller={seller} afterDisplay={afterDisplay} />
                    </div>
                </div>
                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.flexCol,
                        dibsCss.prLarger,
                        styles.leftSection
                    )}
                >
                    <div
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.sassyFontBodySizeSmall,
                            dibsCss.mbSmall
                        )}
                    >
                        <div
                            className={classnames(
                                styles.detailsSellerStatsColumn,
                                dibsCss.basis50,
                                dibsCss.minW50
                            )}
                        >
                            <PdpSharedDealerDistinction seller={seller} />
                        </div>
                        <div
                            className={classnames(
                                styles.detailsSellerStatsColumn,
                                dibsCss.basis50,
                                dibsCss.minW50
                            )}
                        >
                            <PdpSharedDealerInfo viewer={viewer} seller={seller} />
                        </div>
                    </div>
                    {afterDisplay && (
                        <Suspense fallback={null}>
                            <PdpSharedSellerDetailsReviewContainer
                                viewer={viewer}
                                seller={seller}
                            />
                        </Suspense>
                    )}
                </div>
            </>
        ) : (
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.flexCol,
                    dibsCss.prLarger,
                    styles.leftSection
                )}
            >
                <div
                    className={classnames(dibsCss.flex, dibsCss.flexCol, dibsCss.mbMedium)}
                    data-tn="pdp-dealer-info"
                >
                    <PdpSharedSellerBrandingInfo
                        seller={seller}
                        showSellerBranding={showSellerBranding}
                        stackedLayout={showSellerBranding}
                        titleClassName={showSellerBranding ? dibsCss.sassyFontHeader : undefined}
                        imageClassName={sellerBrandingImageClassName}
                        trackingTrigger={
                            showSellerBranding
                                ? triggerConstants.TRIGGER_SELLER_INFORMATION
                                : undefined
                        }
                    >
                        <PdpSharedDealerDetailsHeader
                            viewer={viewer}
                            seller={seller}
                            user={user}
                            afterDisplay={afterDisplay}
                            showSellerBranding={showSellerBranding}
                        />
                    </PdpSharedSellerBrandingInfo>
                </div>
                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.sassyFontBodySizeSmall,
                        dibsCss.mbSmall
                    )}
                >
                    <div
                        className={classnames(
                            styles.detailsSellerStatsColumn,
                            dibsCss.basis50,
                            dibsCss.minW50
                        )}
                    >
                        <PdpSharedDealerDistinction seller={seller} />
                    </div>
                    <div
                        className={classnames(
                            styles.detailsSellerStatsColumn,
                            dibsCss.basis50,
                            dibsCss.minW50
                        )}
                    >
                        <PdpSharedDealerInfo viewer={viewer} seller={seller} />
                    </div>
                </div>
                {afterDisplay && (
                    <Suspense fallback={null}>
                        <PdpSharedSellerDetailsReviewContainer viewer={viewer} seller={seller} />
                    </Suspense>
                )}
            </div>
        );

    return (
        <>
            <div
                className={classnames(
                    styles.wrapper,
                    dibsCss.flex,
                    dibsCss.mtLarge,
                    dibsCss.flexWrap,
                    dibsCss.pLarge,
                    dibsCss.border,
                    dibsCss.borderSolid,
                    dibsCss.borderDolphin,
                    {
                        [styles.hideBorder]: hideBorder,
                    }
                )}
                ref={setWrapperRef}
                data-tn={dataTn}
            >
                <VisibilityTracker
                    elementRef={sellerDetailsRef}
                    onVisibilityChange={({ isVisible }) => {
                        setLoadForms(isVisible);
                        trackSellerDetailsView(isNegotiable, isVisible);
                    }}
                />
                {header}

                <div
                    className={classnames(
                        styles.formContainer,
                        dibsCss.grow,
                        dibsCss.plLarger,
                        dibsCss.borderL,
                        dibsCss.borderLSolid,
                        dibsCss.borderLDolphin,
                        styles.rightSection,
                        {
                            [styles.hideBorder]: hideBorder,
                        }
                    )}
                    data-tn="pdp-contact-dealer-info"
                >
                    {shouldLoadEmbeddedMakeOffer && loadForms ? (
                        <Suspense fallback={''}>
                            <PdpSharedSellerTabsVariantLazy
                                viewer={viewer}
                                item={item}
                                sku={sku}
                                user={user}
                                buttonProps={{ size: 'medium' }}
                                currency={currency}
                                tabOrder={
                                    user?.isVerifiedTrade
                                        ? [CONTACT_SELLER, MAKE_OFFER]
                                        : [MAKE_OFFER, CONTACT_SELLER]
                                }
                            />
                        </Suspense>
                    ) : (
                        <div className={showHeaderBanner ? dibsCss.relative : undefined}>
                            {showBuyerIncentivesBanner ? (
                                <div
                                    className={classnames(
                                        dibsCss.absolute,
                                        dibsCss.right0,
                                        styles.buyerIncentivesBanner
                                    )}
                                >
                                    <PdpSharedBuyerIncentivesCompact user={user} />
                                </div>
                            ) : null}
                            {showFallbackSellerAwayBanner ? (
                                <div
                                    className={classnames(
                                        dibsCss.absolute,
                                        dibsCss.right0,
                                        styles.buyerIncentivesBanner
                                    )}
                                >
                                    <SellerAwayBanner
                                        seller={seller}
                                        messageType="regular"
                                        trackingTrigger="seller info"
                                    />
                                </div>
                            ) : null}
                            <div className={showHeaderBanner ? dibsCss.ptMedium : undefined}>
                                <ContactDealerInlineFormLoader
                                    viewer={viewer}
                                    load={shouldLoadContactForm && loadForms}
                                    item={item}
                                    user={user}
                                    responseTimePos="none"
                                    inputProps={{
                                        hasAnimatedPlaceholder: true,
                                        placeholder: intl.formatMessage(messages.namePlaceholder),
                                    }}
                                    buttonProps={{
                                        className: styles.contactDealerButton,
                                    }}
                                />
                            </div>
                        </div>
                    )}
                    {shouldLoadContactForm && loadForms && (
                        <PdpSharedCallDealerCta
                            item={item}
                            user={user}
                            showSellerBranding={isSellerBrandingV2Variant}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export const PdpRespDealerDetails = createFragmentContainer(PdpRespDealerDetailsComponent, {
    viewer: graphql`
        fragment PdpRespDealerDetails_viewer on Viewer {
            ...ContactDealerInlineForm_viewer @include(if: $afterDisplay)
            ...PdpSharedSellerTabsVariant_viewer
            ...PdpSharedSellerDetailsReviewContainer_viewer @include(if: $afterDisplay)
            ...PdpSharedDealerDetailsHeader_viewer
            ...PdpSharedDealerInfo_viewer
        }
    `,
    user: graphql`
        fragment PdpRespDealerDetails_user on User {
            isVerifiedTrade
            ...ContactDealerInlineForm_user
            ...PdpSharedSellerTabsVariant_user
            ...PdpSharedCallDealerCta_user
            ...PdpSharedDealerDetailsHeader_user
            ...PdpSharedBuyerIncentivesCompact_user
        }
    `,
    item: graphql`
        fragment PdpRespDealerDetails_item on Item {
            ... @include(if: $fetchPdp) {
                ...PdpSharedCallDealerCta_item
                ...ContactDealerInlineForm_item
                ...PdpSharedSellerTabsVariant_item
                dealerDetailsDisplayInfo: displayInfo {
                    ctas {
                        showMakeOffer
                    }
                }
            }
            seller {
                activeTimeAway {
                    endDate
                }
                ...SellerAwayBanner_seller
                ...PdpSharedSellerDetailsReviewContainer_seller @include(if: $afterDisplay)
                ...PdpSharedDealerDetailsBio_seller
                ...PdpSharedDealerDetailsHeader_seller
                ...PdpSharedSellerBrandingInfo_seller
                ...PdpSharedDealerDistinction_seller
                ...PdpSharedDealerInfo_seller
                isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
                liveStorefrontProfile {
                    aboutUsUnbranded
                }
                isAccessible
            }
        }
    `,
    sku: graphql`
        fragment PdpRespDealerDetails_sku on ItemSkuType {
            ...PdpSharedSellerTabsVariant_sku
        }
    `,
});
